// pages/EstimationPointMethodPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Calculator, TrendingUp, Award, AlertCircle, Shield } from "lucide-react";

export default function EstimationPointMethodPage() {
  const navigate = useNavigate();
  const { artworkId } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  const locale = useMemo(() => {
    const language = i18n.resolvedLanguage || i18n.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
  }, [i18n.language, i18n.resolvedLanguage]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }),
    [locale]
  );

  const formatCurrency = (value) =>
    Number.isFinite(Number(value)) ? currencyFormatter.format(Number(value)) : "—";

  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ height: "", width: "", depth: "" });
  const [artistCote, setArtistCote] = useState(5);
  const [is3D, setIs3D] = useState(false);
  const [result, setResult] = useState(null);
  const [hasDimensions, setHasDimensions] = useState(false);

  useEffect(() => {
    if (!artworkId || !user?.id) return;

    const loadArtwork = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("artworks")
          .select("*")
          .eq("id", artworkId)
          .single();

        if (error) throw error;
        setArtwork(data);

        const dims = data?.specifications?.dimensions;
        if (dims?.height && dims?.width) {
          setDimensions({
            height: String(dims.height),
            width: String(dims.width),
            depth: dims.depth ? String(dims.depth) : "",
          });
          setHasDimensions(true);
          setIs3D(Boolean(dims.depth && Number(dims.depth) > 0));
        } else {
          setHasDimensions(false);
        }
      } catch (error) {
        console.error("[EstimationPointMethod] load artwork:", error);
        toast.error(t("estimationPointMethod.errors.load", { message: error.message }));
      } finally {
        setLoading(false);
      }
    };

    loadArtwork();
  }, [artworkId, user?.id, t]);

  const calculateEstimation = () => {
    const height = parseFloat(dimensions.height);
    const width = parseFloat(dimensions.width);
    const depth = is3D ? parseFloat(dimensions.depth) : 0;
    const rating = parseFloat(artistCote);

    if (Number.isNaN(height) || Number.isNaN(width) || Number.isNaN(rating)) {
      toast.warning(t("estimationPointMethod.errors.requiredFields"));
      return;
    }

    if (is3D && Number.isNaN(depth)) {
      toast.warning(t("estimationPointMethod.errors.depthRequired"));
      return;
    }

    const sum = is3D ? height + width + depth : height + width;
    const estimatedPrice = sum * rating;

    setResult({
      sum,
      cote: rating,
      estimatedPrice: Math.round(estimatedPrice),
      lowRange: Math.round(estimatedPrice * 0.8),
      highRange: Math.round(estimatedPrice * 1.2),
    });
  };

  const getCoteCategory = (rating) => {
    if (rating <= 10) return { key: "young", color: "blue" };
    if (rating <= 100) return { key: "regional", color: "green" };
    if (rating <= 1000) return { key: "national", color: "purple" };
    if (rating <= 10000) return { key: "international", color: "orange" };
    return { key: "iconic", color: "red" };
  };

  const category = getCoteCategory(Number(artistCote));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600 font-light">{t("estimationPointMethod.loading")}</p>
        </div>
      </div>
    );
  }

  if (!hasDimensions) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <Link to="/estimation" className="flex items-center gap-2 text-gray-600 hover:text-black mb-4 font-light">
              <ChevronLeft size={20} aria-hidden="true" />
              {t("estimationPointMethod.actions.backToEstimations")}
            </Link>
            <h1 className="text-4xl font-light tracking-tight mb-2">{t("estimationPointMethod.title")}</h1>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-6 py-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-8 rounded-sm">
            <div className="flex items-start gap-4">
              <AlertCircle size={32} className="text-yellow-600 flex-shrink-0" aria-hidden="true" />
              <div>
                <h2 className="text-xl font-semibold text-yellow-900 mb-3">
                  {t("estimationPointMethod.missingDimensions.title")}
                </h2>
                <p className="text-yellow-800 mb-4 font-light leading-relaxed">
                  {t("estimationPointMethod.missingDimensions.description")}
                </p>
                {artwork && (
                  <div className="bg-white bg-opacity-60 rounded p-4 mb-6">
                    <p className="font-medium mb-2">{artwork.title || t("estimationPointMethod.common.untitled")}</p>
                    {artwork.artist && <p className="text-sm text-gray-700">{artwork.artist}</p>}
                  </div>
                )}
                <div className="flex gap-4">
                  <Link
                    to={`/artwork/${artworkId}/edit`}
                    className="px-6 py-3 bg-yellow-600 text-white rounded-sm hover:bg-yellow-700 transition font-light"
                  >
                    {t("estimationPointMethod.missingDimensions.add")}
                  </Link>
                  <Link
                    to={`/artwork/${artworkId}`}
                    className="px-6 py-3 border border-yellow-600 text-yellow-800 rounded-sm hover:bg-yellow-100 transition font-light"
                  >
                    {t("estimationPointMethod.actions.backToArtwork")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link to="/estimation" className="flex items-center gap-2 text-gray-600 hover:text-black mb-4 font-light">
            <ChevronLeft size={20} aria-hidden="true" />
            {t("estimationPointMethod.actions.backToEstimations")}
          </Link>
          <h1 className="text-4xl font-light tracking-tight mb-2">{t("estimationPointMethod.title")}</h1>
          <p className="text-gray-600 font-light">{t("estimationPointMethod.formula")}</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-sm border border-gray-200 p-8">
            <div className="flex items-center gap-2 mb-6">
              <Calculator size={24} strokeWidth={1.5} aria-hidden="true" />
              <h2 className="text-2xl font-light">{t("estimationPointMethod.parameters.title")}</h2>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">{t("estimationPointMethod.parameters.artworkType")}</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setIs3D(false)}
                  className={`flex-1 px-4 py-3 border rounded-sm transition font-light ${
                    !is3D ? "border-black bg-black text-white" : "border-gray-300 hover:border-black"
                  }`}
                >
                  {t("estimationPointMethod.parameters.type2d")}
                </button>
                <button
                  type="button"
                  onClick={() => setIs3D(true)}
                  className={`flex-1 px-4 py-3 border rounded-sm transition font-light ${
                    is3D ? "border-black bg-black text-white" : "border-gray-300 hover:border-black"
                  }`}
                >
                  {t("estimationPointMethod.parameters.type3d")}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                {t("estimationPointMethod.parameters.dimensions")}
                <span className="text-xs text-green-600 ml-2">{t("estimationPointMethod.parameters.prefilled")}</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  value={dimensions.height}
                  onChange={(event) => setDimensions({ ...dimensions, height: event.target.value })}
                  placeholder={t("estimationPointMethod.parameters.height")}
                  aria-label={t("estimationPointMethod.parameters.height")}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-black font-light"
                />
                <input
                  type="number"
                  value={dimensions.width}
                  onChange={(event) => setDimensions({ ...dimensions, width: event.target.value })}
                  placeholder={t("estimationPointMethod.parameters.width")}
                  aria-label={t("estimationPointMethod.parameters.width")}
                  className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-black font-light"
                />
                {is3D && (
                  <div className="col-span-2">
                    <input
                      type="number"
                      value={dimensions.depth}
                      onChange={(event) => setDimensions({ ...dimensions, depth: event.target.value })}
                      placeholder={t("estimationPointMethod.parameters.depth")}
                      aria-label={t("estimationPointMethod.parameters.depth")}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-black font-light"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3">
                {t("estimationPointMethod.parameters.artistRating")}:{" "}
                <span className="text-2xl font-bold">{artistCote}</span>
              </label>
              <input
                type="range"
                min="1"
                max="1000"
                step="1"
                value={artistCote}
                onChange={(event) => setArtistCote(event.target.value)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-2 font-light">
                <span>1</span><span>100</span><span>1000</span>
              </div>
            </div>

            <div className={`p-4 rounded-sm mb-6 border-l-4 ${
              category.color === "blue" ? "bg-blue-50 border-blue-500" :
              category.color === "green" ? "bg-green-50 border-green-500" :
              category.color === "purple" ? "bg-purple-50 border-purple-500" :
              category.color === "orange" ? "bg-orange-50 border-orange-500" :
              "bg-red-50 border-red-500"
            }`}>
              <div className="flex items-start gap-2">
                <Award size={20} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-sm mb-1">
                    {t(`estimationPointMethod.categories.${category.key}.label`)}
                  </p>
                  <p className="text-xs font-light">
                    {t(`estimationPointMethod.categories.${category.key}.description`)}
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={calculateEstimation}
              className="w-full px-6 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition font-light"
            >
              {t("estimationPointMethod.actions.calculate")}
            </button>
          </div>

          <div className="bg-white rounded-sm border border-gray-200 p-8">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={24} strokeWidth={1.5} aria-hidden="true" />
              <h2 className="text-2xl font-light">{t("estimationPointMethod.result.title")}</h2>
            </div>

            {!result ? (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎨</div>
                <p className="text-gray-500 font-light">{t("estimationPointMethod.result.empty")}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-sm p-6">
                  <p className="text-sm text-gray-600 mb-3 font-light">{t("estimationPointMethod.result.calculation")}</p>
                  <div className="font-mono text-sm space-y-1">
                    <p>({dimensions.height} + {dimensions.width}{is3D ? ` + ${dimensions.depth}` : ""}) × {result.cote}</p>
                    <p className="text-gray-500">= {result.sum} × {result.cote}</p>
                    <p className="text-xl font-bold mt-3">= {formatCurrency(result.estimatedPrice)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2 font-light">{t("estimationPointMethod.result.indicativePrice")}</p>
                  <p className="text-4xl font-light mb-2">{formatCurrency(result.estimatedPrice)}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-3 font-light">{t("estimationPointMethod.result.range")}</p>
                  <div className="flex items-center justify-between text-lg">
                    <span className="font-light">{formatCurrency(result.lowRange)}</span>
                    <span className="text-gray-400">—</span>
                    <span className="font-light">{formatCurrency(result.highRange)}</span>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-900 font-light leading-relaxed">
                    <strong>{t("estimationPointMethod.validation.title")}</strong><br />
                    {t("estimationPointMethod.validation.standard")}<br />
                    {t("estimationPointMethod.validation.premium")}<br />
                    {t("estimationPointMethod.validation.responseTime")}
                  </p>
                </div>

                <div className="pt-6 border-t space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      const estimationData = {
                        method: "points",
                        estimatedValue: result.estimatedPrice,
                        lowRange: result.lowRange,
                        highRange: result.highRange,
                        cote: result.cote,
                        dimensions,
                        is3D,
                      };
                      navigate(`/estimation/demande-validation/${artworkId}`, { state: { estimationData } });
                    }}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition font-light flex items-center justify-center gap-2"
                  >
                    <Shield size={20} aria-hidden="true" />
                    {t("estimationPointMethod.actions.requestValidation")}
                  </button>

                  <Link
                    to={`/artwork/${artworkId}`}
                    className="block w-full px-6 py-3 border border-gray-300 text-center rounded-sm hover:border-black transition font-light"
                  >
                    {t("estimationPointMethod.actions.backToArtwork")}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
